import { getRandomString, LocalDocument } from '@/lib'
import { useAuthStore } from './data/auth'
import type { LoginResponse, VerifyTokenResponse, StoredAuthData, ToastInterface } from './type'
import type { UserInfo } from './data/type'
import { Network } from '@capacitor/network'
import { isApp, networkStatus } from '..'
import { App } from '.'
import { RC } from './constant/responseCodes'
import {
  AUTH_MESSAGES,
  LOGIN_ERROR_CODE_MAP,
  API_401_ERROR_MAP,
  HTTP_STATUS_ERROR_MAP,
  REQUIRED_AGREEMENTS,
} from './constant/messages'
import { PUBLIC_PATHS } from './constant'
import { useUserStore } from './store/user'
import { SyncManager } from './sync/syncManager'
import { stopSyncTimer, startSyncTimer } from './sync/backgroundSync'
import { suspendSync, resumeSync } from './sync/suspend'

const LocalDocumentLoginKey = 'Contents-Tokens'
const LocalDocumentAuthDataKey = 'Auth-Data'

// 매뉴얼(사용 설명서) 데모 API 핸들러 - undefined 반환 시 실서버로 통과
export type DemoApiHandler = (method: string, url: string, body?: unknown) => unknown | undefined

let demoApiHandler: DemoApiHandler | null = null
// 데모 핸들러가 유효한 경로 (이 경로를 벗어나면 핸들러가 남아 있어도 무시)
const DEMO_HANDLER_PATH = '/quality/manual'

/**
 * 데모 핸들러 등록 (매뉴얼 페이지 진입 시)
 * 핸들러가 전역 httpRequest에 걸리므로, 등록되어 있는 동안 백그라운드 동기화를 정지시킨다.
 * (정지하지 않으면 실제 업로드가 데모 echo 응답으로 성공 처리되어 서버 미반영 데이터가 유실됨)
 * @param handler 등록할 데모 핸들러
 */
export function setDemoApiHandler(handler: DemoApiHandler): void {
  if (demoApiHandler === handler) return
  // 기존 핸들러가 있으면 그 suspend를 넘겨받는 형태 (중복 suspend 방지)
  if (!demoApiHandler) suspendSync()
  demoApiHandler = handler
  stopSyncTimer()
  // 이미 진행 중인 사이클도 중단 (다음 checkAbort 지점에서 빠져나감)
  SyncManager.abortRunningSync()
}

/**
 * 데모 핸들러 해제 (매뉴얼 페이지 이탈 시 - 다른 핸들러가 이미 등록됐다면 유지)
 * @param handler 등록했던 핸들러 (본인 것만 해제)
 */
export function clearDemoApiHandler(handler: DemoApiHandler): void {
  if (demoApiHandler !== handler) return
  demoApiHandler = null
  resumeSync()
  // 매뉴얼 진입 시 멈춘 타이머 복구 (로그인 상태에서만)
  if (useAuthStore().isLoggedIn) startSyncTimer()
}

// 데모 핸들러가 현재 요청을 가로챌 수 있는 상태인지 확인
function isDemoHandlerActive(): boolean {
  if (!demoApiHandler) return false
  // unmount 훅 유실 등으로 핸들러가 남아도 매뉴얼 밖에서는 절대 가로채지 않도록 경로로 재확인
  return window.location.href.includes(DEMO_HANDLER_PATH)
}
export class ApiError extends Error {
  statusCode: number
  // 서버 검증 거절 항목 (결함 저장 400 응답의 rejected — 어느 항목이 왜 거절됐는지)
  rejected?: unknown[]

  constructor(message: string, statusCode: number, rejected?: unknown[]) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    if (Array.isArray(rejected)) this.rejected = rejected

    // TypeScript에서 Error 상속 시 프로토타입 체인이 끊기는 문제를 방지
    Object.setPrototypeOf(this, ApiError.prototype)
  }
}

function wrapApiError(url: string, method: string, ex: unknown): never {
  if (ex instanceof ApiError) {
    // 이미 ApiError로 래핑된 경우 추가 가공 없이 던짐
    throw ex
  }

  const prefix = `[${method} API ERROR : ${url}]`
  if (ex instanceof Error) {
    throw new Error(`${prefix} ${ex.message}`)
  }
  throw new Error(`${prefix} 알 수 없는 오류`)
}
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace AppAuth {
  const _event = {
    onLoginFailed: (_e: Error): void => {},
    onLogout: (_e: Error): void => {},
  }

  // 초기화 상태 관리
  let _initPromise: Promise<void> | null = null
  let _isInitialized = false
  // 동시 refresh 요청 중복 방지
  let _activeRefreshPromise: Promise<boolean> | null = null

  /**
   * 로그인 응답 데이터 유효성 검증
   * @param token - 토큰
   * @param currentUser - 사용자 정보
   * @param expires_at - 만료 시간
   * @param refresh_token - 리프레시 토큰 (선택)
   * @param refresh_expires_at - 리프레시 토큰 만료 시간 (선택)
   */
  function validateAuthData(
    token: string | undefined,
    currentUser: UserInfo | undefined,
    expires_at: string | undefined,
    refresh_token?: string | undefined,
    refresh_expires_at?: string | undefined,
  ): boolean {
    const basicValid = !!(token && currentUser && expires_at)
    // refresh_token이 있으면 refresh_expires_at도 있어야 함
    if (refresh_token) {
      return basicValid && !!refresh_expires_at
    }
    return basicValid
  }

  /**
   * 인증 데이터 저장
   * @param token - 토큰
   * @param currentUser - 사용자 정보
   * @param expires_at - 만료 시간
   * @param refresh_token - 리프레시 토큰 (선택)
   * @param refresh_expires_at - 리프레시 토큰 만료 시간 (선택)
   */
  async function saveAuthData(
    token: string,
    currentUser: UserInfo,
    expires_at: string,
    refresh_token?: string,
    refresh_expires_at?: string,
  ): Promise<void> {
    const authStore = useAuthStore()
    authStore.setAuthData(token, currentUser, expires_at, refresh_token, refresh_expires_at)

    const authData: StoredAuthData = {
      token,
      refreshToken: refresh_token || '',
      user: currentUser,
      expiresAt: expires_at,
      refreshExpiresAt: refresh_expires_at || '',
    }
    await LocalDocument.set<StoredAuthData>(LocalDocumentAuthDataKey, authData)

    // 하위 호환성을 위해 token만 별도 저장
    await LocalDocument.set<string>(LocalDocumentLoginKey, token)
  }

  /**
   * 에러 리스너 설정
   * @param errorName - 에러 이름
   * @param callback - 콜백 함수
   */
  export function setErrorListener(
    errorName: keyof typeof _event,
    callback: (e: Error) => void,
  ): void {
    _event[errorName] = callback
  }

  /**
   * 앱 마운트 시 인증 확인 및 토큰 검증
   */
  export async function onMounted(): Promise<void> {
    // 이미 초기화 중이거나 완료되었으면 기존 Promise 반환
    if (_initPromise) return _initPromise

    // 초기화 Promise 생성
    _initPromise = (async (): Promise<void> => {
      if (_isInitialized) return

      const path = window.location.href
      const isPublicPath = PUBLIC_PATHS.some(p => path.includes(p))

      const authStore = useAuthStore()

      // 저장된 인증 데이터 로드
      const authData = await LocalDocument.get<StoredAuthData>(LocalDocumentAuthDataKey)

      if (!authData || !authData.token) {
        // 레거시 토큰 확인 (하위 호환성)
        const legacyToken = await LocalDocument.get<string>(LocalDocumentLoginKey)
        if (legacyToken) {
          if (isPublicPath) {
            // public 경로에서는 레거시 토큰 정리만 수행
            LocalDocument.del(LocalDocumentLoginKey)
          } else {
            // 레거시 토큰이 있으면 검증 시도
            authStore.setAuthData(
              legacyToken,
              {} as UserInfo,
              new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            )
            const isValid = await verifyToken()
            if (isValid) {
              _isInitialized = true
              return
            }
            // 검증 실패시 정리
            authStore.clearAuthData()
          }
        }
        _isInitialized = true
        return
      }

      // Store에 저장
      authStore.setAuthData(
        authData.token,
        authData.user,
        authData.expiresAt,
        authData.refreshToken,
        authData.refreshExpiresAt,
      )

      // 토큰 만료 확인
      if (authStore.isTokenExpired) {
        // 리프레시 토큰이 유효하면 토큰 갱신 시도
        if (!authStore.isRefreshTokenExpired && authData.refreshToken) {
          const refreshed = await refreshToken()
          if (refreshed) {
            _isInitialized = true
            return
          }
        }

        // 리프레시 토큰도 만료되었거나 갱신 실패
        if (isPublicPath) {
          // public 경로에서는 리다이렉트 없이 토큰 데이터만 정리
          authStore.clearAuthData()
          LocalDocument.del(LocalDocumentAuthDataKey)
          LocalDocument.del(LocalDocumentLoginKey)
        } else {
          logout({
            type: 'error',
            message: AUTH_MESSAGES.SESSION_EXPIRED_LOGOUT,
          })
        }
        _isInitialized = true
        return
      }

      // 토큰 유효성 검증 (선택적 - 백그라운드에서, public 경로 제외)
      if (!isPublicPath) {
        verifyToken().catch(err => {
          console.error('토큰 검증 실패:', err)
        })
      }

      _isInitialized = true
    })()

    return _initPromise
  }

  /**
   * 초기화 상태 확인
   * @returns {boolean} 초기화 완료 여부
   */
  export function isInitialized(): boolean {
    return _isInitialized
  }

  /**
   * AppAuth 초기화 보장 (router guard에서 사용)
   */
  export async function ensureInitialized(): Promise<void> {
    if (_isInitialized) return
    if (_initPromise) return _initPromise
    return onMounted()
  }

  /**
   * 로그인 토큰 존재 여부 확인
   */
  export async function hasLoginToken(): Promise<boolean> {
    const authData = await LocalDocument.get<StoredAuthData>(LocalDocumentAuthDataKey)
    if (authData && authData.token) return true

    // 레거시 토큰 확인 (하위 호환성)
    const legacyToken = await LocalDocument.get<string>(LocalDocumentLoginKey)
    return legacyToken ? true : false
  }

  /**
   * 관리자 권한 확인 (동기적 체크)
   * authStore를 단일 소스로 사용
   * @returns {boolean} 관리자 권한 여부
   */
  export function isAdmin(): boolean {
    const authStore = useAuthStore()
    return authStore.currentUser?.isAdmin ?? false
  }

  /**
   * 인증 시스템 fetch 공통 함수
   */
  async function authFetch(endpoint: string, data: unknown): Promise<LoginResponse> {
    const loginSystemUrl = import.meta.env.VITE_CLOUD_LOGIN_SYSTEM
    const response = await fetch(`${loginSystemUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    return response.json()
  }

  /**
   * 네트워크 에러 메시지 생성
   */
  function getNetworkErrorMessage(defaultMessage: string): string {
    return !networkStatus.value.status ? AUTH_MESSAGES.NETWORK_ERROR : defaultMessage
  }

  /**
   * 로그인 응답 처리 공통 함수
   */
  async function processLoginResponse(
    responseData: LoginResponse,
  ): Promise<{ success: boolean; data?: LoginResponse['data'] }> {
    if (responseData.statusCode !== 200 || !responseData.data.token) {
      return { success: false }
    }

    const { token, refresh_token, currentUser, expires_at, refresh_expires_at } = responseData.data

    if (!validateAuthData(token, currentUser, expires_at, refresh_token, refresh_expires_at)) {
      _event.onLoginFailed(new Error(AUTH_MESSAGES.INVALID_AUTH_INFO))
      return { success: false }
    }

    await saveAuthData(token!, currentUser!, expires_at!, refresh_token, refresh_expires_at)

    // 로그인 성공 시 초기화 완료로 표시
    _isInitialized = true
    if (!_initPromise) {
      _initPromise = Promise.resolve()
    }

    return { success: true, data: responseData.data }
  }

  /**
   * 로그인 에러 처리
   */
  function handleLoginError(responseData: LoginResponse): void {
    const { code, statusCode, message } = responseData

    console.error('[LOGIN ERROR]', { statusCode, code, message })

    // 인증 에러 (401)
    if (statusCode === 401) {
      const errorMessage =
        LOGIN_ERROR_CODE_MAP[code as keyof typeof LOGIN_ERROR_CODE_MAP] ||
        AUTH_MESSAGES.LOGIN_FAILED
      _event.onLoginFailed(new Error(errorMessage))
      return
    }

    // 기타 에러
    _event.onLoginFailed(new Error(HTTP_STATUS_ERROR_MAP[statusCode] || AUTH_MESSAGES.LOGIN_FAILED))
  }

  /**
   * 사용자 로그인
   * @param id - 사용자 ID
   * @param password - 비밀번호
   * @param successToast - 성공 시 토스트 메시지 (optional)
   */
  export async function login(
    id: string,
    password: string,
    successToast?: ToastInterface,
  ): Promise<boolean> {
    if (!id || !password) {
      _event.onLoginFailed(new Error(AUTH_MESSAGES.LOGIN_REQUIRED_FIELDS))
      return false
    }

    try {
      const responseData = await authFetch('login', { login_id: id, password })

      const result = await processLoginResponse(responseData)
      if (!result.success) {
        handleLoginError(responseData)
        return false
      }

      // 성공 토스트 메시지 표시
      if (successToast) {
        App.toastInstance.add(successToast.type, successToast.message)
      }

      return true
    } catch {
      _event.onLoginFailed(new Error(getNetworkErrorMessage(AUTH_MESSAGES.LOGIN_FAILED)))
      return false
    }
  }

  /**
   * 이메일 인증번호 발송
   * @param email - 이메일 주소
   */
  export async function sendEmailCode(email: string): Promise<boolean> {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      _event.onLoginFailed(new Error(AUTH_MESSAGES.EMAIL_REQUIRED))
      return false
    }

    try {
      const responseData = await authFetch('mail/send-code', { email })

      if (responseData.statusCode === 200) {
        return true
      }

      _event.onLoginFailed(new Error(responseData.message || AUTH_MESSAGES.EMAIL_CODE_SEND_FAILED))
      return false
    } catch {
      _event.onLoginFailed(new Error(getNetworkErrorMessage(AUTH_MESSAGES.EMAIL_CODE_SEND_FAILED)))
      return false
    }
  }

  /**
   * 이메일 인증번호 확인 및 로그인/회원가입
   * @param email - 이메일 주소
   * @param code - 인증번호
   */
  export async function verifyEmailCode(
    email: string,
    code: string,
  ): Promise<{ success: boolean; needRegist?: boolean }> {
    if (!email || !code) {
      _event.onLoginFailed(new Error(AUTH_MESSAGES.EMAIL_CODE_REQUIRED))
      return { success: false }
    }

    try {
      const responseData = await authFetch('mail/verify-code', { email, code })

      if (responseData.statusCode === 200) {
        // needRegist가 true면 신규 사용자
        if (responseData.data.needRegist === true) {
          return { success: true, needRegist: true }
        }

        // 기존 사용자는 바로 로그인 처리
        const result = await processLoginResponse(responseData)
        if (!result.success) {
          return { success: false }
        }

        return { success: true, needRegist: false }
      }

      _event.onLoginFailed(new Error(responseData.message || AUTH_MESSAGES.EMAIL_CODE_MISMATCH))
      return { success: false }
    } catch {
      _event.onLoginFailed(new Error(getNetworkErrorMessage(AUTH_MESSAGES.EMAIL_VERIFY_FAILED)))
      return { success: false }
    }
  }

  /**
   * 이메일 회원가입
   * @param email - 이메일 주소
   * @param code - 인증번호
   * @param agrees - 동의한 약관 목록 (예: ['TERMS_OF_SERVICE', 'PRIVACY_POLICY'])
   */
  export async function emailSignUp(
    email: string,
    code: string,
    agrees: string[],
  ): Promise<{ success: boolean; tokenExpired?: boolean }> {
    // 필수 약관 체크
    const hasAllRequiredAgreements = REQUIRED_AGREEMENTS.every(agreement =>
      agrees.includes(agreement),
    )
    if (!hasAllRequiredAgreements) {
      _event.onLoginFailed(new Error(AUTH_MESSAGES.SIGNUP_TERMS_REQUIRED))
      return { success: false }
    }

    try {
      const responseData = await authFetch('mail/signup', { email, code, agrees })

      // 인증번호 만료
      if (responseData.code === RC.INVALID_TOKEN) {
        _event.onLoginFailed(new Error(AUTH_MESSAGES.EMAIL_CODE_EXPIRED))
        return { success: false, tokenExpired: true }
      }

      const result = await processLoginResponse(responseData)
      if (!result.success) {
        _event.onLoginFailed(new Error(responseData.message || AUTH_MESSAGES.SIGNUP_FAILED))
        return { success: false }
      }

      return { success: true }
    } catch {
      _event.onLoginFailed(new Error(getNetworkErrorMessage(AUTH_MESSAGES.SIGNUP_FAILED)))
      return { success: false }
    }
  }

  // 토큰 갱신
  export async function refreshToken(): Promise<boolean> {
    if (_activeRefreshPromise) return _activeRefreshPromise

    _activeRefreshPromise = (async (): Promise<boolean> => {
      const authStore = useAuthStore()
      const currentRefreshToken = authStore.getRefreshToken()

      if (!currentRefreshToken) {
        console.error('리프레시 토큰이 없습니다.')
        return false
      }

      if (authStore.isRefreshTokenExpired) {
        console.error('리프레시 토큰이 만료되었습니다.')
        return false
      }

      try {
        const apiUrl = import.meta.env.VITE_API_URL
        const response = await fetch(`${apiUrl}refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: currentRefreshToken }),
        })
        const responseData: LoginResponse = await response.json()

        const result = await processLoginResponse(responseData)
        if (!result.success) {
          console.error('토큰 갱신 실패:', responseData.message)
          return false
        }

        return true
      } catch (error) {
        console.error('토큰 갱신 중 오류:', error)
        return false
      } finally {
        _activeRefreshPromise = null
      }
    })()

    return _activeRefreshPromise
  }

  // 로그아웃 시 보존할 localStorage 키 (접두사 매칭)
  const PRESERVED_LS_PREFIXES = [
    'CLIENT_ID',
    'codepush_',
    'network_last_connect',
    'bug_recovery_',
    'onboarding-',
  ]

  /**
   * 사용자별 localStorage 항목 클리어 (디바이스 레벨 키는 보존)
   */
  function clearUserLocalStorage(): void {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      const shouldPreserve = PRESERVED_LS_PREFIXES.some(prefix => key.startsWith(prefix))
      if (!shouldPreserve) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  }

  /**
   * 사용자 로그아웃
   * @param toast - 토스트 메시지 (선택, 기본값: undefined)
   */
  export function logout(toast?: ToastInterface): void {
    try {
      // 백그라운드 동기화 타이머 중지
      stopSyncTimer()

      // LocalDocument 정리
      LocalDocument.del(LocalDocumentLoginKey)
      LocalDocument.del(LocalDocumentAuthDataKey)

      // Store 정리
      const authStore = useAuthStore()
      const userStore = useUserStore()
      authStore.clearAuthData()
      userStore.deleteUserData()

      // 동기화 queue 초기화
      SyncManager.clearSyncStatus()

      // 사용자별 localStorage 클리어 (lastEditorRoute, CURRENT_SYNC_TASK_IDS 등)
      clearUserLocalStorage()

      // 초기화 상태 리셋 (재로그인 대비)
      _isInitialized = false
      _initPromise = null

      // 토스트 메시지가 있으면 표시
      if (toast) {
        App.toastInstance.add(toast.type, toast.message)
      }

      _event.onLogout(new Error(toast?.message || AUTH_MESSAGES.LOGOUT))
    } catch {
      console.error(AUTH_MESSAGES.LOGOUT_ERROR)
    }
  }

  export async function withdrawUser(): Promise<void> {
    const authStore = useAuthStore()
    let token: string | null | undefined = authStore.getToken()
    if (!token) {
      token = await LocalDocument.get<string>(LocalDocumentLoginKey)
    }

    try {
      const loginSystemUrl = import.meta.env.VITE_CLOUD_LOGIN_SYSTEM
      const response = await fetch(`${loginSystemUrl}withdraw`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Contents-Tokens': token || '',
          Authorization: 'Bearer ' + getRandomString(100, false),
        },
        body: JSON.stringify({}),
      })

      const responseData: LoginResponse = await response.json()

      if (responseData.statusCode === 200) {
        logout({
          type: 'success',
          message: AUTH_MESSAGES.WITHDRAW_SUCCESS,
        })
        return
      }
      throw new Error(responseData.message || AUTH_MESSAGES.WITHDRAW_FAILED)
    } catch (error) {
      console.error('회원 탈퇴 중 오류:', error)
      throw new Error(getNetworkErrorMessage(AUTH_MESSAGES.WITHDRAW_FAILED))
    }
  }

  /**
   * 토큰 유효성 검증 (백엔드 API 호출)
   */
  export async function verifyToken(): Promise<boolean> {
    const authStore = useAuthStore()
    const token = authStore.getToken()

    if (!token) return false

    try {
      const verifyUrl = import.meta.env.VITE_CLOUD_LOGIN_SYSTEM
      if (!verifyUrl) {
        console.info('Token 검증 url이 설정되지 않았습니다. 검증을 건너뜁니다.')
        return true
      }
      if (isApp() && !(await Network.getStatus()).connected) {
        console.info('앱환경: 네트워크 연결이 없습니다. 검증을 건너뜁니다.')
        return true
      }

      const response = await fetch(`${verifyUrl}verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })

      const responseData: VerifyTokenResponse = await response.json()

      if (responseData.statusCode === 200 && responseData.data) {
        const { user, expires_at } = responseData.data

        // Store 업데이트
        authStore.updateUser(user)
        authStore.updateExpiresAt(expires_at)

        // LocalDocument 업데이트 (기존 refreshToken 유지)
        const refreshToken = authStore.getRefreshToken()
        const refreshExpiresAt = authStore.refreshExpiresAt
        const authData: StoredAuthData = {
          token,
          refreshToken,
          user,
          expiresAt: expires_at,
          refreshExpiresAt: refreshExpiresAt || '',
        }
        await LocalDocument.set<StoredAuthData>(LocalDocumentAuthDataKey, authData)

        return true
      }

      // 검증 실패 시 PUBLIC_PATHS에서는 조용히 정리
      const currentPath = window.location.href
      const isPublicPath = PUBLIC_PATHS.some(p => currentPath.includes(p))
      if (isPublicPath) {
        authStore.clearAuthData()
        LocalDocument.del(LocalDocumentAuthDataKey)
        LocalDocument.del(LocalDocumentLoginKey)
      } else {
        logout()
      }
      return false
    } catch (error) {
      console.error(AUTH_MESSAGES.TOKEN_VERIFY_FAILED, error)
      // 네트워크 에러 시에도 PUBLIC_PATHS 체크
      const currentPath = window.location.href
      const isPublicPath = PUBLIC_PATHS.some(p => currentPath.includes(p))
      if (isPublicPath) {
        authStore.clearAuthData()
        LocalDocument.del(LocalDocumentAuthDataKey)
        LocalDocument.del(LocalDocumentLoginKey)
      } else {
        logout()
      }
      return false
    }
  }

  // 요청 옵션 (timeoutMs 미지정 시 기존과 동일하게 무제한 대기)
  export interface RequestOptions {
    timeoutMs?: number
    // 성공 응답에 실린 검증 거절 목록(묶음 일부만 거절된 경우)을 받는다 — 응답 본문(data)만 돌려주는 구조라 따로 넘긴다
    onRejected?: (rejected: unknown[]) => void
  }

  /**
   * HTTP 요청 공통 함수
   * @param method - HTTP 메서드
   * @param url - 요청 URL
   * @param body - 요청 본문 (선택)
   * @param retryCount - 현재 재시도 횟수 (기본값: 0)
   * @param maxRetries - 최대 재시도 횟수 (기본값: 1)
   * @param options - 요청 옵션 (timeoutMs: 타임아웃 ms, opt-in)
   */
  async function httpRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    body?: unknown,
    retryCount = 0,
    maxRetries = 1,
    options?: RequestOptions,
  ): Promise<T> {
    // 매뉴얼(사용 설명서) 데모 핸들러 - 응답하면 실서버로 나가지 않음 (토큰 검사보다 먼저)
    if (demoApiHandler && isDemoHandlerActive()) {
      const handled = demoApiHandler(method, url, body)
      if (handled !== undefined) {
        // 스토어가 응답 객체를 변형해도 픽스처 원본이 오염되지 않도록 복제
        return structuredClone(handled) as T
      }
    }

    const authStore = useAuthStore()
    const currentPath = window.location.href
    const isPublicPath = PUBLIC_PATHS.some(p => currentPath.includes(p))

    // 토큰 만료 확인 (재시도 횟수 제한)
    if (authStore.isTokenExpired && retryCount < maxRetries) {
      // 리프레시 토큰이 유효하면 토큰 갱신 시도
      if (!authStore.isRefreshTokenExpired) {
        const refreshed = await refreshToken()
        if (refreshed) {
          // 갱신 성공 시 재시도
          return httpRequest<T>(method, url, body, retryCount + 1, maxRetries, options)
        }
      }

      // 갱신 실패 시 로그아웃 전에 저장소 재확인:
      // 리프레시 토큰은 1회용(rotation)이라 다른 탭이 먼저 갱신했으면
      // 이 탭의 갱신은 거부됨 - 그 탭이 저장해 둔 새 토큰을 채택해 재시도
      const storedAuthData = await LocalDocument.get<StoredAuthData>(LocalDocumentAuthDataKey)
      if (
        storedAuthData?.token &&
        storedAuthData.token !== authStore.getToken() &&
        storedAuthData.expiresAt &&
        new Date(storedAuthData.expiresAt) > new Date()
      ) {
        console.info('[Auth] 다른 탭에서 갱신된 토큰 발견 - 채택 후 재시도')
        authStore.setAuthData(
          storedAuthData.token,
          storedAuthData.user,
          storedAuthData.expiresAt,
          storedAuthData.refreshToken,
          storedAuthData.refreshExpiresAt,
        )
        return httpRequest<T>(method, url, body, retryCount + 1, maxRetries, options)
      }

      // 리프레시 실패 또는 리프레시 토큰도 만료
      if (isPublicPath) {
        // public 경로에서는 리다이렉트 없이 토큰 데이터만 정리
        authStore.clearAuthData()
        LocalDocument.del(LocalDocumentAuthDataKey)
        LocalDocument.del(LocalDocumentLoginKey)
      } else {
        logout({
          type: 'error',
          message: AUTH_MESSAGES.SESSION_EXPIRED_LOGOUT,
        })
      }
      wrapApiError(url, method, new Error(AUTH_MESSAGES.SESSION_EXPIRED))
    }

    // 토큰 가져오기 (Store 우선, 없으면 LocalDocument 조회)
    let token: string | null | undefined = authStore.getToken()
    if (!token) {
      token = await LocalDocument.get<string>(LocalDocumentLoginKey)
      if (token) {
        // 조회한 토큰을 Store에 캐싱
        authStore.updateToken(token)
      }
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Contents-Tokens': token || '',
          Authorization: 'Bearer ' + getRandomString(100, false),
        },
        body: body ? JSON.stringify(body) : undefined,
        signal:
          options?.timeoutMs && typeof AbortSignal.timeout === 'function'
            ? AbortSignal.timeout(options.timeoutMs)
            : undefined,
      })

      const result = await response.json()

      if (result.statusCode !== 200) {
        console.error('[API ERROR]', {
          method,
          url,
          statusCode: result.statusCode,
          code: result.code,
          message: result.message,
        })

        // 401 인증 에러 처리 (재시도 횟수 제한)
        if (result.statusCode === 401 && retryCount < maxRetries) {
          // 리프레시 토큰으로 갱신 시도
          if (!authStore.isRefreshTokenExpired) {
            const refreshed = await refreshToken()
            if (refreshed) {
              // 갱신 성공 시 원래 요청 재시도
              return httpRequest<T>(method, url, body, retryCount + 1, maxRetries, options)
            }
          }

          // 리프레시 실패
          if (isPublicPath) {
            // public 경로에서는 리다이렉트 없이 토큰 데이터만 정리
            authStore.clearAuthData()
            LocalDocument.del(LocalDocumentAuthDataKey)
            LocalDocument.del(LocalDocumentLoginKey)
          } else {
            const logoutMessage =
              API_401_ERROR_MAP[result.code as keyof typeof API_401_ERROR_MAP] ||
              AUTH_MESSAGES.SESSION_EXPIRED_LOGOUT
            logout({ type: 'error', message: logoutMessage })
          }
          wrapApiError(url, method, new Error(AUTH_MESSAGES.AUTH_FAILED))
        }
        throw new ApiError(
          `[API ERROR] ${result.message} at ${url}`,
          result.statusCode,
          result.rejected,
        )
      }

      if (options?.onRejected && Array.isArray(result.rejected) && result.rejected.length > 0) {
        options.onRejected(result.rejected)
      }
      return result.data ?? result.message
    } catch (ex: unknown) {
      wrapApiError(url, method, ex)
    }
  }

  // POST
  export async function Post<T>(url: string, body: unknown, options?: RequestOptions): Promise<T> {
    return httpRequest<T>('POST', url, body, 0, 1, options)
  }

  // GET
  export async function Get<T>(url: string, options?: RequestOptions): Promise<T> {
    return httpRequest<T>('GET', url, undefined, 0, 1, options)
  }

  // PUT
  export async function Put<T>(url: string, body: unknown, options?: RequestOptions): Promise<T> {
    return httpRequest<T>('PUT', url, body, 0, 1, options)
  }

  // DELETE
  export async function Delete<T>(
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return httpRequest<T>('DELETE', url, body, 0, 1, options)
  }
}
